/**
 * AddressAutocomplete — reusable progressive-narrowing address picker.
 *
 * Flow: State pill (optional) → Postcode chip (optional) → Address typeahead
 * Plus a "Can't find it? Enter manually" toggle for PO boxes, brand-new
 * estates not yet indexed by OSM/Google, rural addresses, etc.
 *
 * Each step narrows the next, dramatically improving result quality and
 * cutting API calls. Falls back to "just type" mode if user skips.
 *
 * Props:
 *   value: { place_id, label, lat, lng, state, postcode, suburb,
 *             house_number?, road?, manual? } | null
 *   onChange(value): called with the chosen address
 *   defaultState: optional preselected state code
 *   placeholder: optional input placeholder text
 *   testIdPrefix: optional, defaults to 'addr'
 *   allowManual: defaults to true. Set false to hide the manual mode.
 *   onModeChange: optional, called with 'search' | 'manual' when toggled
 */
import { useEffect, useRef, useState } from "react";
import { Search, MapPin, X, Loader2, Edit3, ChevronLeft } from "lucide-react";
import { api } from "@/lib/api";

export default function AddressAutocomplete({
  value,
  onChange,
  defaultState = "",
  placeholder = "Start typing a street address…",
  testIdPrefix = "addr",
  allowManual = true,
  onModeChange,
}) {
  const [mode, setMode] = useState(value?.manual ? "manual" : "search");
  const [state, setState] = useState(defaultState || value?.state || "");
  const [postcode, setPostcode] = useState(value?.postcode || "");
  const [postcodeQuery, setPostcodeQuery] = useState("");
  const [postcodeOpts, setPostcodeOpts] = useState([]);
  const [showPostcode, setShowPostcode] = useState(false);
  const [query, setQuery] = useState(value?.label || "");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState([]);

  // Manual entry fields
  const [manual, setManual] = useState({
    line1: value?.manual ? (value.line1 || value.road || "") : "",
    line2: value?.manual ? (value.line2 || "") : "",
    suburb: value?.suburb || "",
    state: value?.state || defaultState || "QLD",
    postcode: value?.postcode || "",
    is_po_box: value?.is_po_box || false,
  });

  const debounceRef = useRef(null);

  useEffect(() => {
    api.get("/addr/states").then(({ data }) => setStates(data.items || []))
      .catch(() => setStates([]));
  }, []);

  useEffect(() => { onModeChange?.(mode); }, [mode, onModeChange]);

  // Postcode lookahead — 3-digit minimum so the dropdown is tight.
  useEffect(() => {
    if (!postcodeQuery || postcodeQuery.length < 3) {
      setPostcodeOpts([]);
      return;
    }
    api.get("/addr/postcodes", {
      params: { q: postcodeQuery, state: state || "" }
    }).then(({ data }) => setPostcodeOpts(data.items || []))
      .catch(() => setPostcodeOpts([]));
  }, [postcodeQuery, state]);

  // Debounced address autocomplete
  useEffect(() => {
    if (mode !== "search") return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 3) { setItems([]); return; }
    if (value?.label === query) return;
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/addr/autocomplete", {
          params: { q: query, state: state || "", postcode: postcode || "" },
        });
        setItems(data.items || []);
        setOpen(true);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 280);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, state, postcode, value?.label, mode]);

  const handlePick = async (item) => {
    setLoading(true);
    try {
      const { data } = await api.post("/addr/resolve", {
        place_id: item.place_id, label: item.label,
        lat: item.lat, lng: item.lng,
      });
      const resolved = {
        place_id: data.place_id, label: data.label,
        lat: data.lat, lng: data.lng,
        state: data.state || item.state,
        postcode: data.postcode || item.postcode,
        suburb: data.suburb || item.suburb,
        house_number: item.house_number, road: item.road,
        manual: false,
      };
      onChange?.(resolved);
      setQuery(resolved.label);
      setItems([]); setOpen(false);
    } catch (e) {
      console.warn("resolve failed", e);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setQuery(""); setItems([]); onChange?.(null);
  };

  const handleManualUpdate = (next) => {
    setManual(next);
    const label = [
      next.is_po_box ? `PO Box ${next.line1}` : next.line1,
      next.line2, next.suburb, next.state, next.postcode,
    ].filter(Boolean).join(", ");
    onChange?.({
      place_id: `manual:${Date.now()}`,
      label,
      lat: value?.lat ?? null, lng: value?.lng ?? null,
      state: next.state, postcode: next.postcode,
      suburb: next.suburb,
      line1: next.line1, line2: next.line2,
      is_po_box: next.is_po_box,
      manual: true,
    });
  };

  if (mode === "manual") {
    return (
      <div className="addr-ac addr-manual" data-testid={`${testIdPrefix}-root`}>
        <div className="addr-manual-header">
          <button type="button" className="addr-mode-back"
                     onClick={() => setMode("search")}
                     data-testid={`${testIdPrefix}-back-to-search`}>
            <ChevronLeft size={14} /> Back to search
          </button>
          <label className="addr-pobox-toggle"
                    data-testid={`${testIdPrefix}-pobox-toggle`}>
            <input type="checkbox" checked={manual.is_po_box}
                      onChange={e => handleManualUpdate(
                        { ...manual, is_po_box: e.target.checked })}
                      data-testid={`${testIdPrefix}-pobox-checkbox`} />
            PO Box address
          </label>
        </div>
        <div className="addr-manual-grid">
          <input
            placeholder={manual.is_po_box ? "PO Box number"
                                                  : "Street number & name"}
            value={manual.line1}
            onChange={e => handleManualUpdate({ ...manual, line1: e.target.value })}
            data-testid={`${testIdPrefix}-manual-line1`}
            className="addr-manual-input addr-manual-full"
          />
          {!manual.is_po_box && (
            <input
              placeholder="Unit/Apt (optional)"
              value={manual.line2}
              onChange={e => handleManualUpdate({ ...manual, line2: e.target.value })}
              data-testid={`${testIdPrefix}-manual-line2`}
              className="addr-manual-input addr-manual-full"
            />
          )}
          <input
            placeholder="Suburb / Town"
            value={manual.suburb}
            onChange={e => handleManualUpdate({ ...manual, suburb: e.target.value })}
            data-testid={`${testIdPrefix}-manual-suburb`}
            className="addr-manual-input"
          />
          <select
            value={manual.state}
            onChange={e => handleManualUpdate({ ...manual, state: e.target.value })}
            data-testid={`${testIdPrefix}-manual-state`}
            className="addr-manual-input"
          >
            {states.map(s => <option key={s.code} value={s.code}>{s.code}</option>)}
          </select>
          <input
            placeholder="Postcode"
            value={manual.postcode}
            maxLength={4}
            onChange={e => handleManualUpdate({
              ...manual, postcode: e.target.value.replace(/\D/g, "") })}
            data-testid={`${testIdPrefix}-manual-postcode`}
            className="addr-manual-input"
          />
        </div>
        {!manual.is_po_box && (
          <p className="addr-manual-hint">
            Manual addresses skip the GPS lookup — flood checks unavailable
            for this entry. Use search mode where possible.
          </p>
        )}
        <style>{ADDR_STYLES}</style>
      </div>
    );
  }

  return (
    <div className="addr-ac" data-testid={`${testIdPrefix}-root`}>
      <div className="addr-narrow-row">
        <select
          className="addr-pill addr-state"
          value={state}
          onChange={(e) => { setState(e.target.value); setPostcode(""); setPostcodeQuery(""); }}
          data-testid={`${testIdPrefix}-state`}
        >
          <option value="">All states</option>
          {states.map(s => <option key={s.code} value={s.code}>{s.code}</option>)}
        </select>

        <div className="addr-pc-wrap">
          <input
            className="addr-pill addr-pc"
            placeholder="Postcode"
            maxLength={4}
            value={postcode || postcodeQuery}
            onChange={e => {
              const v = e.target.value.replace(/\D/g, "");
              if (postcode) setPostcode("");
              setPostcodeQuery(v);
              setShowPostcode(true);
            }}
            onFocus={() => setShowPostcode(true)}
            onBlur={() => setTimeout(() => setShowPostcode(false), 180)}
            data-testid={`${testIdPrefix}-postcode`}
          />
          {showPostcode && postcodeOpts.length > 0 && (
            <ul className="addr-pc-dropdown" data-testid={`${testIdPrefix}-postcode-dropdown`}>
              {postcodeOpts.map(p => (
                <li
                  key={p.postcode}
                  onMouseDown={() => {
                    setPostcode(p.postcode);
                    setPostcodeQuery("");
                    if (!state && p.state) setState(p.state);
                    setShowPostcode(false);
                  }}
                  data-testid={`${testIdPrefix}-postcode-option-${p.postcode}`}
                >
                  <strong>{p.postcode}</strong>
                  <span>{p.state}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {(state || postcode) && (
          <button type="button" className="addr-reset"
                     onClick={() => { setState(""); setPostcode(""); setPostcodeQuery(""); }}
                     data-testid={`${testIdPrefix}-reset-narrow`}
                     aria-label="Reset state and postcode filters">
            <X size={14} />
          </button>
        )}

        {allowManual && (
          <button type="button" className="addr-mode-toggle"
                     onClick={() => setMode("manual")}
                     data-testid={`${testIdPrefix}-switch-manual`}
                     title="Enter address manually (PO box, new estate, rural address)">
            <Edit3 size={12} /> Manual
          </button>
        )}
      </div>

      <div className="addr-input-wrap">
        <Search size={16} className="addr-input-icon" />
        <input
          className="addr-input"
          placeholder={placeholder}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => items.length && setOpen(true)}
          onBlur={() => { setTimeout(() => setOpen(false), 180); }}
          data-testid={`${testIdPrefix}-input`}
        />
        {loading && <Loader2 size={16} className="addr-input-spin" />}
        {query && !loading && (
          <button type="button" className="addr-input-clear" onClick={handleClear}
                     data-testid={`${testIdPrefix}-clear`} aria-label="Clear address">
            <X size={14} />
          </button>
        )}
      </div>

      {open && items.length > 0 && (
        <ul className="addr-dropdown" data-testid={`${testIdPrefix}-dropdown`}>
          {items.map((it, i) => (
            <li key={it.place_id} onMouseDown={() => handlePick(it)}
                 data-testid={`${testIdPrefix}-option-${i}`}>
              <MapPin size={14} className="addr-option-icon" />
              <span className="addr-option-label">{it.label}</span>
            </li>
          ))}
        </ul>
      )}

      {open && items.length === 0 && !loading && query.length >= 3 && (
        <div className="addr-empty" data-testid={`${testIdPrefix}-empty`}>
          No matches.{" "}
          {allowManual && (
            <button type="button" className="addr-empty-cta"
                       onClick={() => setMode("manual")}
                       data-testid={`${testIdPrefix}-empty-manual`}>
              Enter manually →
            </button>
          )}
        </div>
      )}

      <style>{ADDR_STYLES}</style>
    </div>
  );
}

const ADDR_STYLES = `
  .addr-ac { position: relative; width: 100%; }
  .addr-narrow-row {
    display: flex; gap: 8px; margin-bottom: 10px; flex-wrap: wrap;
    align-items: center;
  }
  .addr-pill {
    padding: 8px 12px; border-radius: 999px;
    background: rgba(255,255,255,0.06);
    color: var(--sc-text, #f4f6fb);
    border: 1px solid rgba(255,255,255,0.12);
    font-size: 13px; font-family: inherit;
    transition: border-color .2s, background .2s;
  }
  .addr-pill:focus { outline: none; border-color: var(--sc-accent, #ffb24d); background: rgba(255,255,255,0.10); }
  .addr-state { min-width: 90px; cursor: pointer; }
  .addr-pc { width: 90px; text-align: center; letter-spacing: 0.05em; }
  .addr-pc-wrap { position: relative; }
  .addr-pc-dropdown {
    position: absolute; top: 100%; left: 0; right: 0; margin-top: 4px;
    background: #14171f; border: 1px solid rgba(255,255,255,0.12);
    border-radius: 10px; padding: 4px 0; z-index: 30;
    list-style: none; max-height: 220px; overflow-y: auto;
    box-shadow: 0 16px 40px rgba(0,0,0,0.4);
  }
  .addr-pc-dropdown li {
    padding: 8px 12px; display: flex; justify-content: space-between;
    cursor: pointer; color: var(--sc-text, #f4f6fb); font-size: 13px;
    transition: background .15s;
  }
  .addr-pc-dropdown li:hover { background: rgba(255,178,77,0.12); }
  .addr-pc-dropdown li span { color: rgba(244,246,251,0.5); font-size: 11px; }
  .addr-reset, .addr-mode-toggle, .addr-mode-back {
    padding: 8px 12px; border-radius: 999px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.10);
    color: rgba(244,246,251,0.65); cursor: pointer; font-size: 12px;
    display: inline-flex; align-items: center; gap: 6px;
    font-family: inherit; transition: background .15s, color .15s;
  }
  .addr-reset:hover, .addr-mode-toggle:hover, .addr-mode-back:hover {
    background: rgba(255,178,77,0.12); color: var(--sc-accent, #ffb24d);
  }
  .addr-mode-toggle { margin-left: auto; }
  .addr-input-wrap { position: relative; display: flex; align-items: center; }
  .addr-input-icon { position: absolute; left: 14px;
    color: rgba(244,246,251,0.5); pointer-events: none; }
  .addr-input {
    width: 100%; padding: 14px 40px 14px 40px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 12px; color: var(--sc-text, #f4f6fb);
    font-size: 15px; font-family: inherit;
    transition: border-color .2s, background .2s;
  }
  .addr-input:focus { outline: none; border-color: var(--sc-accent, #ffb24d);
    background: rgba(255,255,255,0.08); }
  .addr-input-spin, .addr-input-clear {
    position: absolute; right: 12px; color: rgba(244,246,251,0.5);
    background: transparent; border: none; cursor: pointer;
  }
  .addr-input-spin { animation: addr-spin 1s linear infinite; pointer-events: none; }
  @keyframes addr-spin { to { transform: rotate(360deg); } }
  .addr-dropdown {
    position: absolute; top: calc(100% + 6px); left: 0; right: 0;
    background: #14171f; border: 1px solid rgba(255,255,255,0.12);
    border-radius: 12px; padding: 6px 0; z-index: 30;
    list-style: none; max-height: 280px; overflow-y: auto;
    box-shadow: 0 20px 50px rgba(0,0,0,0.45);
  }
  .addr-dropdown li {
    padding: 12px 14px; display: flex; align-items: flex-start;
    gap: 10px; cursor: pointer; color: var(--sc-text, #f4f6fb);
    font-size: 14px; transition: background .15s;
  }
  .addr-dropdown li:hover { background: rgba(255,178,77,0.1); }
  .addr-option-icon { color: var(--sc-accent, #ffb24d); margin-top: 2px; flex-shrink: 0; }
  .addr-option-label { line-height: 1.4; }
  .addr-empty {
    position: absolute; top: calc(100% + 6px); left: 0; right: 0;
    background: #14171f; border: 1px solid rgba(255,255,255,0.12);
    border-radius: 12px; padding: 14px; z-index: 30;
    color: rgba(244,246,251,0.5); font-size: 13px; text-align: center;
  }
  .addr-empty-cta {
    background: transparent; border: none; color: var(--sc-accent, #ffb24d);
    cursor: pointer; font-family: inherit; font-size: 13px; font-weight: 600;
    text-decoration: underline; margin-left: 6px;
  }
  /* Manual mode */
  .addr-manual { width: 100%; }
  .addr-manual-header { display: flex; justify-content: space-between;
    align-items: center; margin-bottom: 10px; flex-wrap: wrap; gap: 10px; }
  .addr-pobox-toggle { display: inline-flex; align-items: center; gap: 8px;
    font-size: 13px; color: var(--sc-text, #f4f6fb); cursor: pointer; }
  .addr-pobox-toggle input { accent-color: var(--sc-accent, #ffb24d); }
  .addr-manual-grid {
    display: grid; gap: 10px;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  }
  .addr-manual-full { grid-column: 1 / -1; }
  .addr-manual-input {
    padding: 12px 14px; background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 10px; color: var(--sc-text, #f4f6fb);
    font-size: 14px; font-family: inherit; transition: border-color .2s;
  }
  .addr-manual-input:focus { outline: none;
    border-color: var(--sc-accent, #ffb24d);
    background: rgba(255,255,255,0.08); }
  .addr-manual-hint { font-size: 12px; color: rgba(244,246,251,0.5);
    margin: 10px 0 0; }
`;
